import {
	CompositionControllerV1ApiFactory,
	CompositionTemplateEntityType,
	type CompositionTemplateEntry,
	type FilterRelation,
	InvestmentsBulkAnalysisControllerV2ApiFactory,
} from "@eu.mdotm.api/sphere";
import {
	AutoSortHScrollTable,
	CircularProgressBar,
	DebouncedSearchInput,
	Dialog,
	DialogFooter,
	DialogHeader,
	Icon,
	LocalOverlay,
	type Option,
	type OrderBy,
	Row,
	type TableColumn,
	TableDataCell,
	Text,
	useSelectableTableColumn,
} from "@mdotm/shadcn/legacy/components";
import { type MultiSelectCtx, useMultiSelect } from "@mdotm/shadcn/legacy/headless";
import {
	adaptAnimatedNodeProvider,
	type SpawnResult,
	spawn,
	useDebouncedMemo,
} from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";
import { Select, type SelectOption, Button, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import { unreachable } from "@mdotm/utils/runtime";
import { differenceInDays } from "date-fns";
import { test as fuzzyTest } from "fuzzy";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApiGen } from "$root/api/hooks";
import ReactQueryWrapper, { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import Separator from "$root/components/Separator";
import {
	ButtonMultiFilter,
	type CustomFilterOption,
	type CustomFilterValues,
	MultiFilter,
} from "$root/components/SidePanel/MultiFilter";
import SidePanelWithFilters from "$root/components/SidePanel/SidePanelWithFilters";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { statusIconMap } from "$root/pages/PortfoliosStudio/statusIconMap";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { useMappedQuery, useQueryNoRefetch, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { commonColumns } from "$root/ui-lib/common-table-columns";
import { dateWithoutTime, minutes } from "$root/utils";

type FilterKeys = "STATUS" | "TYPE" | "INCEPTION_DATE" | "LAST_STATUS_UPDATE";
type FilterFieldType = "FINITE_SET_STATUSES" | "FINITE_SET_TYPE" | "DATE";
type CopyCompositionFilter = CustomFilterValues<FilterKeys, FilterFieldType>;
type CopyCompositionSidePanelFilter = {
	search: string;
	filters: Array<CopyCompositionFilter>;
};

type CompositionType = Extract<
	CompositionTemplateEntityType,
	"INVESTMENT" | "REFERENCE_INVESTMENT" | "CUSTOM_BENCHMARK"
>;

export type SpawnCopyCompositionDialogProps = {
	uuid?: string;
	entity: CompositionType;
	onSubmitAsync(entity: { uuid: string; type: CompositionTemplateEntityType }): Promise<void>;
	preselectedTemplate?: string;
	initialSidePanelFilters?: CopyCompositionSidePanelFilter;
};

export type CopyCompositionDialogProps = SpawnCopyCompositionDialogProps & MinimumDialogProps;
/** @internal — exported for tests */
export function CopyCompositionDialog(props: CopyCompositionDialogProps) {
	const { show, onAnimationStateChange, onClose, onSubmitAsync, uuid, entity } = props;
	const { t } = useTranslation();

	const [sidePanelFilters, setSidePanelFilters] = useState<CopyCompositionSidePanelFilter>(
		props.initialSidePanelFilters ?? {
			search: "",
			filters: [],
		},
	);

	const selectionCtx = useMultiSelect<string>();

	const compositionApi = useApiGen(CompositionControllerV1ApiFactory);
	const investmentsBulkAnalysisControllerV2Api = useApiGen(InvestmentsBulkAnalysisControllerV2ApiFactory);

	const queryCompositionTemplateRaw = useQueryNoRefetch(["queryCompositionTemplate"], {
		queryFn: () => axiosExtract(compositionApi.getCompositionTemplates(uuid, entity)),
		cacheTime: minutes(5),
		staleTime: 0,
	});

	const queryCompositionTemplate = useMappedQuery(
		({ compositionTemplates }) => {
			if (!compositionTemplates) {
				return { compositionTemplates: [] };
			}

			const remappedCompositionTemplate = compositionTemplates.map(remapCompositionTemplateEntry);

			const preselectedTemplateIndex = remappedCompositionTemplate.findIndex(
				(x) => x.uuid === props.preselectedTemplate,
			);
			if (preselectedTemplateIndex > -1) {
				const shallowCopyTemplateList = [...remappedCompositionTemplate];
				const clonedTemplate = {
					...shallowCopyTemplateList[preselectedTemplateIndex],
				};
				shallowCopyTemplateList.splice(preselectedTemplateIndex, 1);
				shallowCopyTemplateList.unshift(clonedTemplate);
				return { compositionTemplates: shallowCopyTemplateList };
			}

			return { compositionTemplates: remappedCompositionTemplate };
		},
		[props.preselectedTemplate],
		queryCompositionTemplateRaw,
	);

	useQueryReactor(queryCompositionTemplate, {
		onSuccess({ compositionTemplates }) {
			rowsWithoutSearch.forceUpdate(compositionTemplates);
		},
	});

	const availableCompositionTemplate = useMemo(
		() => queryCompositionTemplate.data?.compositionTemplates?.filter((x) => x.uuid && x.available) ?? [],
		[queryCompositionTemplate.data?.compositionTemplates],
	);

	const rowsWithoutSearch = useDebouncedMemo(
		() => {
			const filters = [...sidePanelFilters.filters.filter((x) => x.key && x.value?.length && x.value.length > 0)];
			if (!sidePanelFilters?.filters || sidePanelFilters?.filters.length === 0 || filters.length === 0) {
				return availableCompositionTemplate;
			}
			return availableCompositionTemplate.filter((template) =>
				filters.every((filter) => {
					const valueToMatch = fieldKeyExtractor(filter.key!, template);
					return matchEntities(filter, valueToMatch ? String(valueToMatch) : undefined);
				}),
			);
		},
		[sidePanelFilters.filters, availableCompositionTemplate],
		{ debounceInterval: 500 },
	);

	const filteredCompositionTemplates = useDebouncedMemo(
		() => {
			const filteredInvestments = sidePanelFilters.search
				? rowsWithoutSearch.value.filter((investment) => fuzzyTest(sidePanelFilters.search, investment.name ?? "-"))
				: rowsWithoutSearch.value;

			return filteredInvestments;
		},
		[sidePanelFilters.search, rowsWithoutSearch.value],
		{ debounceInterval: 500 },
	);

	return (
		<Dialog
			size="screen"
			show={show}
			onClose={onClose}
			header={<DialogHeader>Copy composition template</DialogHeader>}
			onAnimationStateChange={onAnimationStateChange}
			onSubmitAsync={async () => {
				const selectedUuid = selectionCtx.selection.first();
				const selectedEntity = availableCompositionTemplate.find((x) => x.uuid === selectedUuid);
				if (!selectedEntity || !selectedEntity.entityType) {
					throw new Error("Unable to match entity");
				}

				await onSubmitAsync({
					uuid: selectedEntity.uuid!,
					type: selectedEntity.entityType,
				});
			}}
			footer={
				<DialogFooter
					primaryAction={
						<SubmitButton disabled={queryCompositionTemplate.isFetching || selectionCtx.selection.size === 0}>
							{t("BUTTON.CONFIRM")}
						</SubmitButton>
					}
					neutralAction={
						<Button palette="tertiary" onClick={onClose}>
							{t("BUTTON.CANCEL")}
						</Button>
					}
				/>
			}
		>
			<div className="h-[calc(100dvh-214px)] flex-1 flex flex-col overflow-hidden">
				<div className="flex-1 flex min-h-0 min-w-0">
					<SidePanelWithFilters<CopyCompositionSidePanelFilter>
						title="Copy composition panel"
						toggleIcon="Filter-no"
						value={sidePanelFilters}
						onChange={setSidePanelFilters}
						filters={[
							{
								component: <Separator text="Filters" align="start" />,
							},
							{
								component({ value, onChange }) {
									return (
										<DebouncedSearchInput
											externalQuery={value.search}
											onChange={(search) => onChange({ ...value, search })}
											placeholder="Search instrument.."
											className="mb-2"
											timeout={500}
											data-qualifier="CopyComposition/SidePanelFilters/Search"
										/>
									);
								},
							},
							{
								component({ value, onChange }) {
									return (
										<>
											<div className="mb-2">
												<MultiFilter
													value={value.filters}
													options={availableTemplateCompositionFilters}
													fieldSetBlock={({ filterType, onChange: onChangeField, value: opt, index }) => {
														if (filterType === "FINITE_SET_STATUSES") {
															return (
																<ReactQueryWrapper
																	queryKey={["queryAsyncFilterOptions"]}
																	queryFn={async () => {
																		const { fieldSet } = await axiosExtract(
																			investmentsBulkAnalysisControllerV2Api.getFieldSet(filterType),
																		);
																		const options: SelectOption<string>[] =
																			fieldSet
																				?.filter((x) => x.label && x.identifier)
																				.map((x) => ({
																					label: x.label!,
																					value: x.identifier!,
																				})) ?? []; // it was identifier before, change in MDOT-18036
																		return { options };
																	}}
																	cacheTime={minutes(10)}
																	loadingFallback={
																		<CircularProgressBar style={{ width: "1rem" }} value="indeterminate" />
																	}
																>
																	{({ options }) => (
																		<Select
																			multi
																			enableSearch
																			options={options}
																			value={opt.value ?? []}
																			onChange={(identifiers) => {
																				if (identifiers) {
																					onChangeField(
																						{
																							...opt,
																							value: identifiers,
																							relation: "EQUAL",
																						},
																						index,
																					);
																				}
																			}}
																			className="w-[139px]"
																		/>
																	)}
																</ReactQueryWrapper>
															);
														}

														if (filterType === "FINITE_SET_TYPE") {
															return (
																<Select
																	multi
																	enableSearch
																	options={entitiesOption.filter((x) => x.value !== "CUSTOM_BENCHMARK")}
																	value={opt.value ?? []}
																	onChange={(x) => {
																		if (x) {
																			onChangeField({ ...opt, value: x, relation: "EQUAL" }, index);
																		}
																	}}
																	className="w-[139px]"
																/>
															);
														}
													}}
													onChange={(item, index) => {
														const next = value.filters.slice();
														next[index] = item;
														onChange({ ...value, filters: next });
													}}
													onDelete={(index) => {
														const next = value.filters.slice();
														next.splice(index, 1);
														onChange({ ...value, filters: next });
													}}
												/>
											</div>
											<ButtonMultiFilter
												onClick={() => {
													const next = value.filters.slice();
													next.push({});
													onChange({ ...value, filters: next });
												}}
											/>
										</>
									);
								},
							},
						]}
					/>
					<div className="flex-1 flex min-h-0 min-w-0 px-4 py-6 relative">
						<LocalOverlay
							show={queryCompositionTemplate.isFetching || filteredCompositionTemplates.state === "debouncing"}
							style={{
								background: filteredCompositionTemplates.value.length === 0 ? "white" : "transparent",
								zIndex: 5,
							}}
						>
							<CircularProgressBar value="indeterminate" />
						</LocalOverlay>
						<ReactQueryWrapperBase query={queryCompositionTemplate} loadingFallback={<></>}>
							{({ compositionTemplates }) => (
								<CopyCompositionTable
									filteredCompositionTemplates={filteredCompositionTemplates.value}
									compositionTemplates={compositionTemplates ?? []}
									onChange={(ctx) => selectionCtx.setSelection(ctx.selection)}
									preselectedTemplate={props.preselectedTemplate}
								/>
							)}
						</ReactQueryWrapperBase>
					</div>
				</div>
			</div>
		</Dialog>
	);
}

type CustomCompositionTemplateEntry = CompositionTemplateEntry & {
	remappedEntityType: CompositionTemplateEntityType;
};
/** @internal — exported for tests */
export function remapCompositionTemplateEntry(entry: CompositionTemplateEntry): CustomCompositionTemplateEntry {
	return {
		...entry,
		remappedEntityType: remapEntityType(entry.entityType!),
	};
}

/** @internal — exported for tests */
export function remapEntityType(entityType: CompositionTemplateEntityType): CompositionTemplateEntityType {
	if (entityType === "CUSTOM_BENCHMARK" || entityType === "REFERENCE_INVESTMENT") {
		return "REFERENCE_INVESTMENT";
	}
	return entityType;
}

const templateEntityToLabel = {
	INVESTMENT: "Portfolio",
	REFERENCE_INVESTMENT: "Reference",
	STANDARD_BENCHMARK: "Template",
	CUSTOM_BENCHMARK: "Benchmark",
} satisfies Record<CompositionTemplateEntityType, string>;

const entitiesOption = Object.values(CompositionTemplateEntityType).map(
	(x): Option<CompositionTemplateEntityType> => ({
		value: x,
		label: templateEntityToLabel[x],
	}),
);

const availableTemplateCompositionFilters = [
	{
		key: "TYPE",
		needApi: true,
		availableRelations: ["EQUAL"],
		type: "FINITE_SET_TYPE",
	},
	{
		key: "STATUS",
		needApi: true,
		availableRelations: ["EQUAL"],
		type: "FINITE_SET_STATUSES",
	},
	{
		key: "LAST_STATUS_UPDATE",
		availableRelations: ["EQUAL", "GREATER_THAN", "LOWER_THAN", "NOT_EQUAL"],
		type: "DATE",
	},
	{
		key: "INCEPTION_DATE",
		availableRelations: ["EQUAL", "GREATER_THAN", "LOWER_THAN", "NOT_EQUAL"],
		type: "DATE",
	},
] satisfies Array<CustomFilterOption<FilterKeys, FilterFieldType>>;

/** @internal — exported for tests */
export function fieldKeyExtractor(key: FilterKeys, entity: CustomCompositionTemplateEntry) {
	switch (key) {
		case "INCEPTION_DATE":
			return entity.creationTime;
		case "LAST_STATUS_UPDATE":
			return entity.modificationTime;
		case "STATUS":
			return entity.status;
		case "TYPE":
			return entity.remappedEntityType;
		default:
			unreachable(key);
	}
}

export function matchEntities(filter: CopyCompositionFilter, fieldValue?: string): boolean {
	switch (filter.type) {
		case "FINITE_SET_STATUSES":
		case "FINITE_SET_TYPE":
			return filter.value ? Boolean(filter.value?.includes(fieldValue ?? "")) : true;
		case "DATE": {
			if (!filter?.value || filter.value.length === 0 || !fieldValue) {
				return true;
			}
			const filterDate = filter.value?.at(0);
			if (!filterDate) {
				return true;
			}
			const difference = fieldValue
				? -differenceInDays(dateWithoutTime(filterDate), dateWithoutTime(fieldValue))
				: undefined;

			return relationMatch(0, difference, filter.relation);
		}
		case undefined:
			return true;
		default:
			unreachable(filter.type);
	}
}

function relationMatch(filterValue?: number, fieldValue?: number, relation?: FilterRelation) {
	return filterValue != null && fieldValue != null
		? (() => {
				switch (relation) {
					case "EQUAL":
						return fieldValue === filterValue;
					case "GREATER_THAN":
						return fieldValue > filterValue;
					case "LOWER_THAN":
						return fieldValue < filterValue;
					case "NOT_EQUAL":
						return fieldValue !== filterValue;
					default:
						return false;
				}
			})()
		: false;
}

function CopyCompositionTable({
	compositionTemplates,
	filteredCompositionTemplates,
	onChange,
	preselectedTemplate,
}: {
	compositionTemplates: CustomCompositionTemplateEntry[];
	filteredCompositionTemplates: CustomCompositionTemplateEntry[];
	onChange(selection: MultiSelectCtx<string>): void;
	preselectedTemplate?: string;
}) {
	const { t } = useTranslation();

	const selectableRowIds = useMemo(() => compositionTemplates.map((x) => x.uuid!), [compositionTemplates]);

	const {
		column: checkBoxColumn,
		rowClassName,
		toggle,
		multiSelectCtx,
	} = useSelectableTableColumn({
		filteredRows: filteredCompositionTemplates,
		rows: compositionTemplates,
		selectBy: (row) => row.uuid ?? "",
		selectableRowIds,
		mode: "radio",
	});

	const columns = useMemo<Array<TableColumn<(typeof compositionTemplates)[number], string>>>(
		() => [
			{
				...checkBoxColumn,
				header: "",
			},
			{
				header: t("TABLE.HEADERS.NAME"),
				content: (row) => row.name ?? "",
				sortFn: builtInSortFnFor("name"),
				name: "name",
				width: 320,
			},
			{
				header: t("TABLE.HEADERS.TYPE"),
				content: (row) => {
					if (
						(row.labelledAsBenchmark && row.entityType === "REFERENCE_INVESTMENT") ||
						row.entityType === "CUSTOM_BENCHMARK"
					) {
						return "Benchmark";
					}
					return row.entityType ? templateEntityToLabel[row.entityType] : "";
				},
				sortFn: builtInSortFnFor("entityType"),
				name: "entityType",
			},
			{
				header: t("TABLE.HEADERS.STATUS"),
				content: ({ status }, cellProps) => {
					const sanitizedStatus = status ? statusIconMap[status] : "-";
					if (sanitizedStatus === "-") {
						return <TableDataCell {...cellProps}>{sanitizedStatus}</TableDataCell>;
					}
					return (
						<Row
							{...cellProps}
							title={sanitizedStatus.title}
							alignItems="center"
							data-qualifier="PortfolioStudio/PortfolioList/Table/Column(Status)"
						>
							<Icon
								className={{
									"mr-1": true,
									// Temporarily (MDOT-18271) mask Error status as Calculating until BE fixes their process
									"rotate-180": status === "ERROR",
								}}
								icon={sanitizedStatus.icon}
								color={themeCSSVars.palette_N800} // changed
								size={sanitizedStatus.size}
							/>
							<Text type="Body/M/Book" className="line-clamp-2">
								{sanitizedStatus.title}
							</Text>
						</Row>
					);
				},
				sortFn: builtInSortFnFor("status"),
				name: "entityStatus",
			},
			{
				...commonColumns.date("creationTime"),
				header: t("TABLE.HEADERS.INCEPTION_DATE"),
				minWidth: 128,
			},
			{
				...commonColumns.date("modificationTime"),
				header: t("TABLE.HEADERS.LAST_STATUS_UPDATE"),
				name: "lastUpdate",
				minWidth: 92,
			},
		],
		[checkBoxColumn, t],
	);

	useEffect(() => {
		onChange(multiSelectCtx);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [multiSelectCtx.selection.first()]);

	useEffect(() => {
		if (preselectedTemplate) {
			multiSelectCtx.setSelection([preselectedTemplate]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [preselectedTemplate]);

	return (
		<AutoSortHScrollTable
			rows={filteredCompositionTemplates}
			columns={columns}
			orderBy={preselectedTemplate == null ? defaultOrderBy : undefined}
			onRowClick={(x) => toggle(x.uuid!)}
			palette="uniform"
			rowClassName={rowClassName}
			pinnedColumns={[{ name: "name", side: "left" }]}
		/>
	);
}

const defaultOrderBy: Array<OrderBy<"name">> = [{ columnName: "name", direction: "asc" }];

export function spawnCopyCompositionDialog(props: SpawnCopyCompositionDialogProps): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<CopyCompositionDialog
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onSubmitAsync={async (uuids) => {
					await props.onSubmitAsync(uuids);
					resolve();
				}}
			/>
		)),
	);
}
